import { useState, useEffect } from "react";
import { X, Download } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if already installed or previously dismissed
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      localStorage.getItem("pwa-install-dismissed")
    ) {
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show banner after a short delay so it doesn't pop up immediately
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem("pwa-install-dismissed", "1");
  };

  if (!showBanner || dismissed) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-in slide-in-from-bottom-4"
      role="dialog"
      aria-label="Install app"
    >
      <div className="bg-[oklch(0.22_0.06_255)] text-white rounded-2xl shadow-2xl p-4 border border-white/10">
        <div className="flex items-start gap-3">
          <div className="bg-[oklch(0.72_0.18_65)] rounded-xl p-2 shrink-0">
            <Download className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Install RAF SERV Pest App</p>
            <p className="text-white/70 text-xs mt-0.5">
              Add to your home screen for quick access to emergency pest control.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="flex-1 bg-[oklch(0.72_0.18_65)] hover:bg-[oklch(0.65_0.18_65)] text-black font-bold py-2 px-3 rounded-lg text-xs transition-colors"
              >
                Install App
              </button>
              <button
                onClick={handleDismiss}
                className="bg-white/10 hover:bg-white/20 text-white py-2 px-3 rounded-lg text-xs transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/50 hover:text-white transition-colors shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
